import { useState } from "react";
import { yerelHaftaBasi } from "@koclukpanel/contracts";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Alan,
  BosDurum,
  Buton,
  Iskelet,
  Kart,
  Rozet,
  SatirEylemi,
  SatirEylemleri,
  Secim,
  cn,
  HAFTA_GUNLERI,
  type RozetTonu,
} from "@koclukpanel/ui";
import { SayfaBasligiEki } from "./Kabuk";
import { OgrenciSecimAlani, useOgrenciListesi } from "./OgrenciSecici";
import { ApiHatasi, mesguliyetEkle, planGuncelle, planIptal, planTanimlari } from "./api";
import {
  GUN_ADLARI,
  KATEGORI_ADI,
  duzenlemeFarki,
  formHatalari,
  formdanGovde,
  haftadakiGunler,
  haftaninGunu,
  tanimdanForm,
  type Kategori,
  type PlanFormu,
  type PlanTanimi,
} from "./plan-tanimi";

/**
 * ÖĞRENCİ HAFTALIK PLANI — F-PPL-07b (04.10.2026, müşteri revizesi madde 5)
 *
 * "Eğitim koçu öğrencinin rutinlerini öğrenip girmelidir." Okul,
 * dershane, özel ders, sosyal aktivite saatleri burada gün × saat olarak
 * giriliyor; program ekranı (F-SCH-11) aynı kayıtları bilgi amaçlı
 * gösteriyor. Ayrı bir tablo YOK: kayıt meşguliyet takviminin kendisi.
 *
 * Kaldırma iptal (`cancelledAt`) — silme değil (CLAUDE.md kural 7).
 */

const KATEGORI_TONU: Record<Kategori, RozetTonu> = {
  SCHOOL: "bilgi",
  COURSE: "uyari",
  PRIVATE_LESSON: "onay",
  SOCIAL: "notr",
  OTHER: "notr",
};

function haftaBasi(kaydirma: number): string {
  const g = yerelHaftaBasi(new Date());
  g.setUTCDate(g.getUTCDate() + kaydirma * 7);
  return g.toISOString().slice(0, 10);
}

function saat(iso: string): string {
  const d = new Date(iso);
  return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
}

const BOS_FORM: PlanFormu = {
  gun: 1,
  tarih: "",
  baslangicSaati: "08:00",
  bitisSaati: "09:00",
  tekrarli: true,
};

/**
 * UNİKOÇ "Öğrenci Haftalık Planları" düzeni (sürüm 9a L2; StudentPlans): ekran
 * AÇILIŞTA tabloyu gösterir, öğrenci üstteki kartta seçilir. Ayrı bir "kimin
 * planı?" sayfası yok. Seçili öğrenci yoksa kapsamdaki İLK öğrenci gösterilir
 * (yalnız bu ekranda — genel öğrenci bağlamı değişmez, son kullanılanlara yazılmaz);
 * kullanıcı listeden seçince bağlam `onOgrenciSec` ile kurulur.
 */
export function OgrenciPlanlari({
  ogrenciId,
  ogrenciAdi,
  duzenleyebilir,
  onGeri,
  onOgrenciSec,
}: {
  ogrenciId: string | null;
  ogrenciAdi?: string;
  /** `takvim.mesguliyet.yonet` — yoksa ekran salt okunur. */
  duzenleyebilir: boolean;
  onGeri?: () => void;
  onOgrenciSec?: (id: string, ad: string) => void;
}) {
  const ogrenciler = useOgrenciListesi();
  const ilk = ogrenciId ? undefined : ogrenciler.data?.ogrenciler[0];
  const etkinId = ogrenciId ?? ilk?.id ?? null;
  const etkinAd = ogrenciId ? (ogrenciAdi ?? "") : ilk ? `${ilk.ad} ${ilk.soyad}` : "";

  return (
    <div className="flex flex-col gap-4">
      <SayfaBasligiEki
        aciklama="Koçlar, öğrenciye ait “Okul / Dershane / Özel Ders / Sosyal Aktivite / Diğer” saatlerini ekler ve yönetir."
        eylemler={
          onGeri ? (
            <Buton gorunum="sessiz" onClick={onGeri}>
              ← İş listesi
            </Buton>
          ) : undefined
        }
      />
      <Kart gorunum="unk" olarak="div">
        <div className="max-w-xl">
          <OgrenciSecimAlani
            secili={etkinId}
            seciliAd={etkinAd}
            onSec={(id, ad) => onOgrenciSec?.(id, ad)}
            yardim="Seçimi yaptığınızda liste yenilenir."
          />
        </div>
      </Kart>
      {etkinId ? (
        <PlanGovdesi key={etkinId} ogrenciId={etkinId} duzenleyebilir={duzenleyebilir} />
      ) : ogrenciler.isPending ? (
        <Iskelet satir={4} />
      ) : (
        <BosDurum
          ikon="ogrenci"
          baslik="Öğrenci seçin"
          aciklama="Plan saatleri bir öğrenciye ait; yukarıdan öğrenci seçtiğinizde haftalık program burada görünür."
        />
      )}
    </div>
  );
}

function PlanGovdesi({
  ogrenciId,
  duzenleyebilir,
}: {
  ogrenciId: string;
  duzenleyebilir: boolean;
}) {
  const qc = useQueryClient();
  const [kaydirma, setKaydirma] = useState(0);
  const hafta = haftaBasi(kaydirma);

  const [form, setForm] = useState<PlanFormu>(BOS_FORM);
  const [kategori, setKategori] = useState<Kategori>("SCHOOL");
  const [aciklama, setAciklama] = useState("");
  const [duzenlenen, setDuzenlenen] = useState<string | null>(null);
  const [kaldirilacak, setKaldirilacak] = useState<string | null>(null);
  const [denendi, setDenendi] = useState(false);

  const liste = useQuery({
    queryKey: ["plan-tanimlari", ogrenciId],
    queryFn: () => planTanimlari(ogrenciId),
    retry: false,
  });

  const yenile = async () => {
    await qc.invalidateQueries({ queryKey: ["plan-tanimlari", ogrenciId] });
    // Program ekranı aynı kayıtları gösteriyor (F-SCH-11).
    await qc.invalidateQueries({ queryKey: ["mesguliyet", ogrenciId] });
  };

  const formuSifirla = () => {
    setForm(BOS_FORM);
    setKategori("SCHOOL");
    setAciklama("");
    setDuzenlenen(null);
    setDenendi(false);
  };

  const kaydet = useMutation({
    mutationFn: async () => {
      if (duzenlenen) {
        // Codex tur 36 B3: düzenleme YALNIZCA değişen alanları gönderir;
        // özgün başlangıç ve tekrar kuralı korunur (`duzenlemeFarki`).
        const eski = (liste.data ?? []).find((t) => t.id === duzenlenen);
        if (!eski) throw new Error("Düzenlenen kayıt listede yok; sayfayı yenileyin.");
        const fark = duzenlemeFarki(eski, form, { kategori, aciklama }, hafta);
        if (Object.keys(fark).length === 0) return null;
        return planGuncelle(duzenlenen, fark);
      }
      const zaman = formdanGovde(form, hafta);
      const ortak = {
        ...zaman,
        kategori,
        aciklama: aciklama.trim() || null,
        // Başlık takvim ve program ekranında görünen ad: açıklama yoksa kategori.
        baslik: aciklama.trim() || KATEGORI_ADI[kategori],
      };
      return mesguliyetEkle({ ogrenciId, ...ortak });
    },
    onSuccess: async () => {
      formuSifirla();
      await yenile();
    },
  });

  const kaldir = useMutation({
    mutationFn: (id: string) => planIptal(id),
    onSuccess: async () => {
      setKaldirilacak(null);
      await yenile();
    },
  });

  const hatalar = formHatalari(form);
  const gecerli = Object.keys(hatalar).length === 0;

  const duzenlemeyeAc = (t: PlanTanimi) => {
    setDuzenlenen(t.id);
    setForm(tanimdanForm(t));
    setKategori(t.kategori ?? "OTHER");
    setAciklama(t.aciklama ?? "");
    setDenendi(false);
  };

  const gunlere = (gun: number) =>
    (liste.data ?? [])
      .filter((t) => haftadakiGunler(t, hafta).includes(gun))
      .sort((a, b) => saat(a.baslangic).localeCompare(saat(b.baslangic)));

  return (
    <div className="flex flex-col gap-4">
      {duzenleyebilir && (
        <Kart
          gorunum="unk"
          olarak="div"
          baslik={duzenlenen ? "Plan kaydını düzenle" : undefined}
          ikon={duzenlenen ? "duzenle" : undefined}
        >
          <form
            aria-label={duzenlenen ? "Plan kaydını düzenle" : "Plan kaydı ekle"}
            className="flex flex-col gap-4"
            onSubmit={(e) => {
              e.preventDefault();
              setDenendi(true);
              if (gecerli) kaydet.mutate();
            }}
          >
            {/* UNİKOÇ sırası: Gün · Kategori · Başlangıç · Bitiş · Tekrarlı mı? */}
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-[1fr_1.5fr_1fr_1fr_1.5fr]">
              {form.tekrarli ? (
                <Secim
                  etiket="Gün"
                  value={String(form.gun)}
                  onChange={(e) => setForm({ ...form, gun: Number(e.target.value) })}
                  hata={denendi ? hatalar.gun : undefined}
                >
                  {GUN_ADLARI.map((g, i) => (
                    <option key={g} value={i + 1}>
                      {g}
                    </option>
                  ))}
                </Secim>
              ) : (
                <Alan
                  etiket="Tarih"
                  type="date"
                  value={form.tarih}
                  onChange={(e) => setForm({ ...form, tarih: e.target.value })}
                  hata={denendi ? hatalar.tarih : undefined}
                />
              )}
              <Secim
                etiket="Kategori"
                value={kategori}
                onChange={(e) => setKategori(e.target.value as Kategori)}
              >
                {(Object.keys(KATEGORI_ADI) as Kategori[]).map((k) => (
                  <option key={k} value={k}>
                    {KATEGORI_ADI[k]}
                  </option>
                ))}
              </Secim>
              <Alan
                etiket="Başlangıç"
                type="time"
                value={form.baslangicSaati}
                onChange={(e) => setForm({ ...form, baslangicSaati: e.target.value })}
                hata={denendi ? hatalar.baslangicSaati : undefined}
              />
              <Alan
                etiket="Bitiş"
                type="time"
                value={form.bitisSaati}
                onChange={(e) => setForm({ ...form, bitisSaati: e.target.value })}
                hata={denendi ? hatalar.bitisSaati : undefined}
              />
              <Secim
                etiket="Tekrarlı mı?"
                value={form.tekrarli ? "evet" : "hayir"}
                onChange={(e) => setForm({ ...form, tekrarli: e.target.value === "evet" })}
              >
                <option value="evet">Her hafta</option>
                <option value="hayir">Tek seferlik</option>
              </Secim>
            </div>
            <Alan
              etiket="Açıklama"
              value={aciklama}
              maxLength={200}
              placeholder="Örn: Matematik Özel Ders"
              onChange={(e) => setAciklama(e.target.value)}
              yardim="İsteğe bağlı. Boşsa kategori adı görünür."
            />
            {kaydet.isError && (
              <p role="alert" className="text-hata-700 text-sm">
                {kaydet.error instanceof ApiHatasi ? kaydet.error.message : "Kaydedilemedi."}
              </p>
            )}
            <div className="flex flex-wrap justify-end gap-2">
              {duzenlenen && (
                <Buton gorunum="kesikli" onClick={formuSifirla}>
                  Vazgeç
                </Buton>
              )}
              <Buton type="submit" yukleniyor={kaydet.isPending}>
                {duzenlenen ? "Kaydet" : "Ekle"}
              </Buton>
            </div>
          </form>
        </Kart>
      )}

      <Kart
        gorunum="unk"
        baslik="Haftalık Program"
        ikon="takvim"
        eylem={
          <div className="flex flex-wrap items-center gap-2">
            <Buton gorunum="ikincil" onClick={() => setKaydirma(kaydirma - 1)}>
              ← Önceki hafta
            </Buton>
            <span className="text-metin-ikincil text-sm tabular-nums">
              {haftaninGunu(hafta, 1).split("-").reverse().join(".")} –{" "}
              {haftaninGunu(hafta, 7).split("-").reverse().join(".")}
            </span>
            <Buton gorunum="ikincil" onClick={() => setKaydirma(kaydirma + 1)}>
              Sonraki hafta →
            </Buton>
            {kaydirma !== 0 && (
              <Buton gorunum="kesikli" onClick={() => setKaydirma(0)}>
                Bu hafta
              </Buton>
            )}
          </div>
        }
      >
        {liste.isPending && <Iskelet satir={4} />}
        {liste.isError && (
          <BosDurum
            ton="hata"
            baslik="Plan alınamadı"
            aciklama={
              liste.error instanceof ApiHatasi
                ? liste.error.message
                : "Bu, öğrencinin planı olmadığı anlamına gelmez."
            }
          />
        )}

        {liste.data && (
          <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-7">
            {GUN_ADLARI.map((ad, i) => {
              const kayitlar = gunlere(i + 1);
              return (
                <section
                  key={ad}
                  aria-label={ad}
                  className="rounded-kart border-mor-kenar bg-notr-0 flex min-w-0 flex-col overflow-hidden border"
                >
                  {/*
                    Gün başlığı UNİKOÇ gün renginde (tek tablo: gun-renkleri.ts). Pazartesi başlar;
                    GUN_ADLARI de Pazartesi'den (i + 1 = ISO gün) — iki dizi aynı sırada.
                  */}
                  <h3
                    className={cn(
                      "text-metin-koyu px-3 py-2 text-sm font-bold",
                      HAFTA_GUNLERI[i]?.zemin,
                    )}
                  >
                    {ad}{" "}
                    <span className="text-metin-ikincil font-normal">
                      {haftaninGunu(hafta, i + 1).slice(8)}.{haftaninGunu(hafta, i + 1).slice(5, 7)}
                    </span>
                  </h3>
                  <div className="flex flex-col gap-2 p-2">
                    {kayitlar.length === 0 ? (
                      <p className="text-notr-600 px-1 py-2 text-xs">Kayıt yok</p>
                    ) : (
                      kayitlar.map((t) => (
                        <article
                          key={t.id}
                          className={cn(
                            "rounded-alan bg-notr-50 border-mor-kenar flex flex-col gap-1.5 border p-2",
                            duzenlenen === t.id && "ring-marka-500 ring-2",
                          )}
                        >
                          <div className="flex flex-wrap items-center gap-1">
                            <Rozet ton={KATEGORI_TONU[t.kategori ?? "OTHER"]}>
                              {KATEGORI_ADI[t.kategori ?? "OTHER"]}
                            </Rozet>
                            {t.tekrarKurali && (
                              <span className="border-notr-200 text-metin-koyu bg-notr-0 rounded-rozet inline-flex items-center border px-1.5 py-0.5 text-[11px] font-semibold">
                                Her hafta
                              </span>
                            )}
                          </div>
                          {/*
                            UNİKOÇ kartında kalem/çöp ikonları saatin ÜSTÜNDE. Kaldırma yine iki
                            adımlı (iptal, silme değil — kural 7): çöp ikonu onay satırını açar.
                          */}
                          {duzenleyebilir && kaldirilacak !== t.id && (
                            <SatirEylemleri>
                              <SatirEylemi
                                tur="duzenle"
                                etiket="Düzenle"
                                onClick={() => duzenlemeyeAc(t)}
                              />
                              <SatirEylemi
                                tur="sil"
                                etiket="Kaldır"
                                onClick={() => setKaldirilacak(t.id)}
                              />
                            </SatirEylemleri>
                          )}
                          <p className="text-metin-koyu text-sm font-bold tabular-nums">
                            {saat(t.baslangic)}–{saat(t.bitis)}
                          </p>
                          {t.aciklama && (
                            <p className="text-metin-ikincil text-xs break-words">{t.aciklama}</p>
                          )}
                          {duzenleyebilir && kaldirilacak === t.id && (
                            <div className="flex flex-wrap items-center gap-1">
                              <span className="text-notr-700 text-xs">Kaldırılsın mı?</span>
                              <Buton
                                gorunum="tehlike"
                                yukleniyor={kaldir.isPending}
                                onClick={() => kaldir.mutate(t.id)}
                              >
                                Kaldır
                              </Buton>
                              <Buton gorunum="sessiz" onClick={() => setKaldirilacak(null)}>
                                Vazgeç
                              </Buton>
                            </div>
                          )}
                        </article>
                      ))
                    )}
                  </div>
                </section>
              );
            })}
          </div>
        )}
      </Kart>
      {kaldir.isError && (
        <p role="alert" className="text-hata-700 text-sm">
          {kaldir.error instanceof ApiHatasi ? kaldir.error.message : "Kaldırılamadı."}
        </p>
      )}
    </div>
  );
}
